import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import TwoColumnLayout from "../layouts/TwoColumnLayout";
import TopNavTabs from "../components/TopNavTabs";
import { apiFetch, getUmatDisplayLabel, getUmatDisplayLabelLine2 } from "../utils/wkCommon";
import { getAvatarPlaceholder } from "../utils/wkCommon";
import { isZh } from "../utils/language";

/* ===============================
   DUTY LEVEL COMPUTE
================================ */
function computeDutyLevel(umatAttrs) {
  if (!umatAttrs) return 0;
  if (umatAttrs.dianchuanshi) return 7;
  if (umatAttrs.jiang_Shi) return 6;
  if (umatAttrs.jiangYuan) return 5;
  if (umatAttrs.tanzhu) return 4;
  if (umatAttrs.qingKou) return 3;
  if (umatAttrs.sd3Hari) return 2;
  return 1;
}

/* ===============================
   RENDER TRISTATE
================================ */
function renderTriState(v) {
  const text =
    v === true
      ? (isZh() ? "已" : "Sudah")
      : v === false
      ? (isZh() ? "未" : "Belum")
      : (isZh() ? "無紀錄" : "Tidak ada catatan");

  if (v === true) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ color: "#16a34a", fontWeight: 700 }}>✔</span>
        {text}
      </span>
    );
  }

  if (v === false) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ color: "#c2410c", fontWeight: 700 }}>✖</span>
        {text}
      </span>
    );
  }

  // null → tanpa icon (lebih clean)
  return (
    <span style={{ color: "#64748b" }}>
      {text}
    </span>
  );
}

/* ===============================
   BADGE CONFIG
================================ */
const DUTY_BADGE = {
  7: { label: "點傳師", color: "#7c2d12", bg: "#fde68a" },
  6: { label: "講師", color: "#92400e", bg: "#fde68a" },
  5: { label: "講員", color: "#92400e", bg: "#fef3c7" },
  4: { label: "壇主", color: "#1e3a8a", bg: "#dbeafe" },
  3: { label: "清口", color: "#065f46", bg: "#d1fae5" },
  2: { label: "辦事", color: "#1f2937", bg: "#e5e7eb" },
  1: { label: "道親", color: "#334155", bg: "#f1f5f9" }
};

/* ===============================
   TAB CONFIG
================================ */
const UMAT_DETAIL_PANEL = {
  identitas: [
    "umatID", "tsfyUmatID", "gender",
    "chineseName", "pinyinName",
    "fullName", "nickName",
    "birthdate"
  ],
  kontak: [
    "mobilePhone", "homePhone", "email",
    "lineID", "address", "district", "area",
    "domisiliSekitarJakarta"
  ],
  latar_belakang: [
    "education", "occupation",
    "hobbiesInterests",
    "sudahVegetarian", "cetyaAtHome"
  ],
  keluarga: [],
  perjalanan_tao: [
    "tanggalMemohonTao",
    "guruPengajakYinShi",
    "guruPenjaminBaoShi",
    "fotangMemohonTao"
  ],
  jabatan: [
    "dianchuanshi", "jiang_Shi",
    "jiangYuan", "tanzhu",
    "qingKou", "sd3Hari"
  ]
};

function formatBirthdateWithAge(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (isNaN(d.getTime())) return value;

  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();

  // usia presisi
  const now = new Date();
  let age = now.getFullYear() - year;
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) {
    age--;
  }

  // ========================
  // Chinese Mode
  // ========================
  if (isZh()) {
    return `${year}年 ${month}月 ${day}日（${age} 歲）`;
  }

  // ========================
  // Bahasa Mode
  // ========================
  const monthNames = [
    "Jan","Feb","Mar","Apr","Mei","Jun",
    "Jul","Agu","Sep","Okt","Nov","Des"
  ];

  const dayPad = String(day).padStart(2, "0");
  const monthShort = monthNames[d.getMonth()];

  return `${dayPad}-${monthShort}-${year} (${age} thn)`;
}

/* ===============================
   FIELD LABEL & FORMAT
================================ */
const UMAT_FIELD_META = {
  umatID: { label: { id: "Nomor Umat (QR Li En)", zh: "禮恩道親編號" } },
  tsfyUmatID: { label: { id: "Nomor Umat (QR Pusat)", zh: "中心道親編號" } },
  displayName: { label: { id: "Nama Tampilan", zh: "顯示名稱" } },
  fullName: { label: { id: "Nama Lengkap", zh: "姓名" } },
  nickName: { label: { id: "Nama Panggilan", zh: "暱稱" } },

  chineseName: { label: { id: "Nama Mandarin", zh: "中文姓名" } },
  pinyinName: { label: { id: "Pinyin", zh: "拼音" } },

  gender: { label: { id: "Jenis Kelamin", zh: "性別" } },

  birthdate: {
    label: { id: "Tanggal Lahir", zh: "出生日期" },
    format: v => formatBirthdateWithAge(v)
  },
  
  mobilePhone: { label: { id: "No. HP", zh: "手機號碼" } },
  homePhone: { label: { id: "Telepon Rumah", zh: "住家電話" } },
  email: { label: { id: "Email", zh: "電子郵件" } },
  lineID: { label: { id: "LINE ID", zh: "LINE 帳號" } },

  address: { label: { id: "Alamat", zh: "住址" } },
  district: { label: { id: "Wilayah", zh: "行政區" } },
  area: {
    label: { id: "Fotang Area", zh: "所屬區域" },
    format: v => {
      if (!v) return "-";
      if (typeof v === "string") return v;
      return v.name || v.shortname || "-";
    }
  },
  domisiliSekitarJakarta: { label: { id: "Domisili Sekitar Jakarta", zh: "雅加達附近居住者" }, format: v => v ?  "是 Ya" : "否 Tidak" },

  education: { label: { id: "Pendidikan", zh: "學歷" } },
  occupation: { label: { id: "Pekerjaan", zh: "職業" } },
  hobbiesInterests: { label: { id: "Hobi / Minat", zh: "興趣" } },

  sudahVegetarian: { label:{ id:  "Sudah Vegetarian", zh: "學吃素" }, format: v => v ? "是 Ya" : "否 Tidak" },
  cetyaAtHome: { label: { id: "Cetya di Rumah", zh: "有家庭佛堂" }, format: v => v ?  "是 Ya" : "否 Tidak" },

  tanggalMemohonTao: {
    label: { id: "Tanggal Memohon Tao", zh: "求道日期" }
  },

  guruPengajakYinShi: { label: { id: "Guru Pengajak（引師）", zh: "引師" } },
  guruPenjaminBaoShi: { label: { id: "Guru Penjamin（保師）", zh: "保師" } },
  fotangMemohonTao: { label: { id: "Fotang Memohon Tao", zh: "求道佛堂" } },

  dianchuanshi: { label: { id: "Dianchuanshi", zh: "點傳師" }, format: v => v ?  "是 Ya" : "否 Tidak" },
  jiang_Shi: { label: { id: "Jiang Shi", zh: "講師" }, format: v => v ?  "是 Ya" : "否 Tidak" },
  jiangYuan: { label: { id: "Jiang Yuan", zh: "講員" }, format: v => v ?  "是 Ya" : "否 Tidak" },
  tanzhu: { label: { id: "Tanzhu", zh: "壇主" }, format: v => v ?  "是 Ya" : "否 Tidak" },
  qingKou: { label: { id: "Qingkou", zh: "清口" }, format: v => v ?  "是 Ya" : "否 Tidak" },
  sd3Hari: { label: { id: "Sidang Dharma 3 Hari", zh: "三天法會" }, format: v => v ?  "是 Ya" : "否 Tidak" }
};

/* ===============================
   HELPER
================================ */
function getParentDisplay(parentRel, name, phone) {
  if (parentRel) {
    return {
      name: getUmatDisplayLabel(parentRel),
      phone: parentRel.mobilePhone || "-"
    };
  }

  return {
    name: name || "-",
    phone: phone || "-"
  };
}

function mapContactPreference(val) {
  if (!val) return null;

  const map = {
    Father: isZh() ? "父親" : "Ayah",
    Mother: isZh() ? "母親" : "Ibu",
    Self: isZh() ? "本人" : "Diri Sendiri",
    Relative: isZh() ? "親屬" : "Keluarga"
  };

  return map[val] || val;
}

function getContactDisplay(data) {
  const rel = data?.relation_contact_preference;

  // jika relasi ada dan punya umat
  if (rel?.umat_related_to) {
    const u = rel.umat_related_to;
    return formatNamePhone(
      getUmatDisplayLabel(u),
      u.mobilePhone
    );
  }

  // fallback ke ortu jika dipilih
  if (data?.ContactPreference === "Father") {
    return formatNamePhone(
      data?.fatherName,
      data?.fatherPhoneNo
    );
  }

  if (data?.ContactPreference === "Mother") {
    return formatNamePhone(
      data?.motherName,
      data?.motherPhoneNo
    );
  }

  // fallback terakhir
  return "-";
}

function KeluargaTab({ umat, familyData, siblings, children, loading, spouse, spouseStatus }) {
  const sortedSiblings = sortSiblingsByAge(siblings, umat.birthdate);

  if (loading) {
    return <div style={{ textAlign: "center" }}>
      {isZh() ? "載入中..." : "Memuat..."}
    </div>;
  }

  const data = familyData || umat;

  const father = getParentDisplay(
    data?.father_umat_item,
    data?.fatherName,
    data?.fatherPhoneNo
  );

  const mother = getParentDisplay(
    data?.mother_umat_item,
    data?.motherName,
    data?.motherPhoneNo
  );

  return (
    <div style={{ fontSize: "0.875rem" }}>
      {/* ===== PASANGAN ===== */}
      {spouse && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "clamp(7rem, 35%, 14rem) 1fr",
              padding: "0.4rem 0",
              borderBottom: "1px dashed #e5e7eb"
            }}
          >
            <div style={{ color: "#64748b", fontWeight: 600 }}>
              {isZh() ? "配偶" : "Nama Pasangan"}
            </div>

            <div>
              {/* baris utama */}
              <div>
                {formatNamePhone(
                  getUmatDisplayLabel(spouse),
                  spouse.mobilePhone
                )}
              </div>

              {/* indikator */}
              <div
                style={{
                  marginTop: "0.2rem",
                  fontSize: "0.8rem",
                  color: spouseStatus === "SYNC" ? "#16a34a" : "#dc2626",
                  fontWeight: 500
                }}
              >
                {spouseStatus === "SYNC"
                  ? (isZh() ? "✔ 已雙向連結" : "✔ Tersambung dua arah")
                  : (isZh() ? "⚠ 尚未同步" : "⚠ Belum sinkron")}
              </div>
            </div>
          </div>
      )}

      {/* ===== ANAK ===== */}
      {(spouse || (children && children.length > 0)) && (
      <Section title={{ id: "Anak", zh: "子女" }}>
        {children && children.length > 0 ? (
          children.map((c, i) => {
            const u = c.attributes || c;

            return (
              <Row
                key={i}
                label={{ id: `Anak ${i+1}`, zh: `子女 ${i+1}` }}
                value={formatNamePhone(
                  getUmatDisplayLabel(u),
                  u.mobilePhone
                )}
              />
            );
          })
        ) : (
          <div style={{ color: "#64748b" }}>
            {isZh() ? "無資料" : "Tidak ada data"}
          </div>
        )}
      </Section>
      )}

      {/* ===== ORANG TUA ===== */}
      <Section title={{ id: "Orang Tua", zh: "父母" }}>
        <Row label={{ id: "Ayah", zh: "父親" }} value={formatNamePhone(father.name, father.phone)} />
        <Row label={{ id: "Ibu", zh: "母親" }} value={formatNamePhone(mother.name, mother.phone)} />
      </Section>

      {/* ===== SAUDARA ===== */}
      <Section title={{ id: "Saudara", zh: "兄弟姐妹" }}>
        {sortedSiblings.length > 0 ? (
          sortedSiblings.map((s, i) => {
            const u = s.attributes || s;

            const relLabel = getSiblingLabel(u.birthdate, umat.birthdate);

            return (
              <Row
                key={i}
                label={{
                  id: relLabel ? `Saudara (${relLabel})` : `Saudara ${i+1}`,
                  zh: relLabel ? `兄弟姐妹 (${relLabel})` : `兄弟姐妹 ${i+1}`
                }}
                value={formatNamePhone(
                  getUmatDisplayLabel(u),
                  u.mobilePhone
                )}
              />
            );
          })
        ) : (
          <div style={{ color: "#64748b" }}>
            {isZh() ? "無資料" : "Tidak ada data"}
          </div>
        )}
      </Section>

      {/* ===== CONTACT PREF ===== */}
      <Section title={{ id: "Kontak Darurat", zh: "緊急聯絡人" }}>

        <Row
          label={{ id: "Relasi", zh: "關係" }}
          value={
            data?.relation_contact_preference?.relationDesc ||
            data?.relation_contact_preference?.relation ||
            mapContactPreference(data?.ContactPreference) ||
            "-"
          }
        />

        <Row
          label={{ id: "Nama Kontak", zh: "聯絡人姓名" }}
          value={getContactDisplay(data)}
        />

      </Section>

    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <div style={{
        fontWeight: 700,
        marginBottom: "0.5rem"
      }}>
        {isZh() ? title.zh : title.id}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  if (!value || value === "- | -") return null;

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "clamp(7rem, 35%, 14rem) 1fr",
      padding: "0.4rem 0",
      borderBottom: "1px dashed #e5e7eb"
    }}>
      <div style={{ color: "#64748b", fontWeight: 600 }}>
        {isZh() ? label.zh : label.id}
      </div>
      <div>{value}</div>
    </div>
  );
}

function sortSiblingsByAge(list, selfBirthdate) {
  if (!Array.isArray(list)) return [];

  return list
    .map((s) => {
      const u = s.attributes || s;
      return {
        raw: s,
        birthdate: u.birthdate ? new Date(u.birthdate) : null
      };
    })
    .sort((a, b) => {
      if (!a.birthdate) return 1;
      if (!b.birthdate) return -1;
      return a.birthdate - b.birthdate; // tua → muda
    })
    .map((x) => x.raw);
}

function getSiblingLabel(siblingBirthdate, selfBirthdate) {
  if (!siblingBirthdate || !selfBirthdate) return null;

  const s = new Date(siblingBirthdate);
  const me = new Date(selfBirthdate);

  if (s < me) {
    return isZh() ? "哥哥 / 姐姐" : "Kakak";
  } else if (s > me) {
    return isZh() ? "弟弟 / 妹妹" : "Adik";
  }
  return null;
}

function formatNamePhone(name, phone) {
  if (name && phone && phone != "-") return `${name} | ${phone}`;
  if (name) return name;
  if (phone) return phone;
  return "-";
}

/* ===============================
   PERJALANAN TAO & JABATAN SUCI
================================ */
function getPerjalananTaoValue(umat, field) {
  const qiu = umat?.qiu_dao_person;

  const q =
    qiu?.attributes ||
    qiu?.data?.attributes ||
    qiu?.data ||
    qiu;

  const map = {
    tanggalMemohonTao: q?.qiuDaoDate,
    guruPengajakYinShi: q?.yinShi,
    guruPenjaminBaoShi: q?.baoShi,
    fotangMemohonTao: q?.qiuDaoLocation
  };

  return map[field] ?? umat[field];
}

function PerjalananTaoSection({ umat }) {

  const baseFields = [
    "tanggalMemohonTao",
    "guruPengajakYinShi",
    "guruPenjaminBaoShi",
    "fotangMemohonTao"
  ];

  const triFields = [
    { key: "sd3Hari", label: { id: "SD 3 Hari", zh: "三天法會" }, desc: "sd3HariDesc", date: "sd3HariDate" },
    { key: "mingde", label: { id: "Ming De", zh: "明德" }, desc: "mingdeDesc" },
    { key: "xinmin", label: { id: "Xin Min", zh: "新民" }, desc: "xinminDesc" },
    { key: "zhishan", label: { id: "Zhi Shan", zh: "至善" }, desc: "zhishanDesc" },
    { key: "xuande", label: { id: "Xuan De", zh: "宣德" }, desc: "xuandeDesc" },
    { key: "hongde", label: { id: "Hong De", zh: "弘德" }, desc: "hongdeDesc" },
  ];

  return (
    <div>

      {/* =========================
          BAGIAN 1: EXISTING (QIU DAO)
      ========================= */}
      {baseFields.map((field) => {
        const raw = getPerjalananTaoValue(umat, field);
        if (!raw) return null;

        const meta = UMAT_FIELD_META[field] || {};
        const label = meta.label
          ? (isZh() ? meta.label.zh : meta.label.id)
          : field;

        const fromQiu = raw !== umat[field];
        const value = fromQiu ? raw + " 🔗" : raw;

        return (
          <div key={field} style={{
            display: "grid",
            gridTemplateColumns: "clamp(7rem, 35%, 14rem) 1fr",
            padding: "0.4rem 0",
            borderBottom: "1px dashed #e5e7eb"
          }}>
            <div style={{ color: "#64748b", fontWeight: 600 }}>
              {label}
            </div>
            <div>{value}</div>
          </div>
        );
      })}

      {/* =========================
          BAGIAN 2: TRISTATE
      ========================= */}
      <div style={{ marginTop: "0.75rem" }} />

      {triFields.map((it) => {
        const val = umat[it.key];
        const desc = umat[it.desc];
        const date = it.date ? umat[it.date] : null;

        return (
          <div key={it.key} style={{
            display: "grid",
            gridTemplateColumns: "clamp(7rem, 35%, 14rem) 1fr",
            padding: "0.5rem 0",
            borderBottom: "1px dashed #e5e7eb"
          }}>
            <div style={{ color: "#64748b", fontWeight: 600 }}>
              {isZh() ? it.label.zh : it.label.id}
            </div>

            <div>
              {/* BOOLEAN */}
              <div style={{ fontWeight: 500 }}>
                {renderTriState(val)}
              </div>

              {/* DESC */}
              {desc && (
                <div style={{ fontSize: "0.85rem", color: "#334155" }}>
                  {desc}
                </div>
              )}

              {/* DATE */}
              {date && (
                <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                  {date}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function JabatanSection({ umat }) {
  const items = [
    { key: "dianchuanshi", label: { id: "Dianchuanshi", zh: "點傳師" } },
    { key: "jiang_Shi", label: { id: "Jiang Shi", zh: "講師" }, desc: "jsDesc" },
    { key: "jiangYuan", label: { id: "Jiang Yuan", zh: "講員" }, desc: "jyDesc" },
    { key: "tanzhu", label: { id: "Tanzhu", zh: "壇主" }, desc: "tzDesc" },
    { key: "qingKou", label: { id: "Qing Kou", zh: "清口" }, desc: "qkDesc", date: "qingkouDate" },
  ];

  // ambil hanya yang TRUE
  const activeItems = items.filter(it => umat[it.key] === true);

  // ✅ fallback jika kosong
  if (activeItems.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "#64748b", padding: "0.75rem 0" }}>
        {isZh() ? "尚無職責資料" : "Belum ada data peringkat tugas."}
      </div>
    );
  }

  return (
    <div>
      {activeItems.map((it) => {
        const desc = it.desc ? umat[it.desc] : null;
        const date = it.date ? umat[it.date] : null;

        return (
          <div key={it.key} style={{
            display: "grid",
            gridTemplateColumns: "clamp(7rem, 35%, 14rem) 1fr",
            padding: "0.5rem 0",
            borderBottom: "1px dashed #e5e7eb"
          }}>
            <div style={{ color: "#64748b", fontWeight: 600 }}>
              {isZh() ? it.label.zh : it.label.id}
            </div>

            <div>
              <div style={{ fontWeight: 500, color: "#16a34a" }}>
                ✔ {isZh() ? "已" : "Sudah"}
              </div>

              {desc && (
                <div style={{ fontSize: "0.85rem", color: "#334155" }}>
                  {desc}
                </div>
              )}

              {date && (
                <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                  {date}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ===============================
   PAGE
================================ */
export default function UmatProfileBook() {
  const history = useHistory();
  const { id } = useParams();

  const [umat, setUmat] = useState(null);
  const [activeTab, setActiveTab] = useState("identitas");
  const [errorMsg, setErrorMsg] = useState(null);

  const TAB_LABELS = {
    identitas: { id: "Identitas", zh: "基本" },
    kontak: { id: "Kontak", zh: "聯絡" },
    latar_belakang: { id: "Latar Belakang", zh: "背景" },
    keluarga: { id: "Keluarga", zh: "家庭成員" },
    perjalanan_tao: { id: "Qiu Dao & Kurikulum", zh: "求道及道學" },
    jabatan: { id: "Peringkat Tugas", zh: "職責" }
  };

  const [spouse, setSpouse] = useState(null);
  const [spouseStatus, setSpouseStatus] = useState(null);

  const [familyData, setFamilyData] = useState(null);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const [children, setChildren] = useState([]);

  useEffect(() => {
    if (activeTab !== "keluarga" || familyData) return;
    let data = null; // ✅ pindahkan ke atas
    async function loadFamily() {
      try {
        setFamilyLoading(true);

        const resp = await apiFetch(
          `/api/umats/${id}?populate[0]=father_umat_item&populate[1]=mother_umat_item&populate[2]=relation_contact_preference&populate[3]=relation_contact_preference.umat_related_to`
        );

        data = resp?.data?.attributes || resp?.data || resp;

        setFamilyData(data);
      } catch (e) {
        console.warn("loadFamily failed", e);
      } finally {
        setFamilyLoading(false);
      }

      // ambil parent id
      const fatherId = data?.father_umat_item?.documentId;
      const motherId = data?.mother_umat_item?.documentId;

      let siblingList = [];

      if (fatherId || motherId) {
        const filters = [];

        if (fatherId) {
          filters.push(`filters%5Bfather_umat_item%5D%5BdocumentId%5D%5B$eq%5D=${fatherId}`);
        }

        if (motherId) {
          filters.push(`filters%5Bmother_umat_item%5D%5BdocumentId%5D%5B$eq%5D=${motherId}`);
        }

        const query = filters.join("&");

        const respSiblings = await apiFetch(
          `/api/umats?${query}&populate[0]=father_umat_item&populate[1]=mother_umat_item`
        );

        const arr = respSiblings?.data || [];

        // exclude diri sendiri
        siblingList = arr.filter(
          (x) => (x.documentId || x.id) !== id
        );
      }

      const map = {};

      siblingList.forEach((s) => {
        const key = s.documentId || s.id;
        if (!map[key]) {
          map[key] = s;
        }
      });

      setSiblings(Object.values(map));

      // ==========================
      // CHILDREN (reverse query)
      // ==========================
      let childList = [];

      if (id) {
        /*
        const respChildren = await apiFetch(
          `/api/umats?filters%5B$or%5D[0]%5Bfather_umat_item%5D%5BdocumentId%5D%5B$eq%5D=${id}&filters%5B$or%5D[1]%5Bmother_umat_item%5D%5BdocumentId%5D%5B$eq%5D=${id}`
        );
        */
       const respChildren = await apiFetch(
          `/api/umats?` +
          `filters%5B$or%5D[0]%5Bfather_umat_item%5D%5BdocumentId%5D%5B$eq%5D=${encodeURIComponent(id)}` +
          `&filters%5B$or%5D[1]%5Bmother_umat_item%5D%5BdocumentId%5D%5B$eq%5D=${encodeURIComponent(id)}` +
          `&sort%5B0%5D=birthdate%3Aasc`
        );

        childList = respChildren?.data || [];
      }

      setChildren(childList);
    }

    loadFamily();
  }, [activeTab, id, familyData]);

  useEffect(() => {
    let mounted = true;

    async function loadUmat() {
      try {
        if (!id) {
          setErrorMsg("ID profil tidak ditemukan.");
          return;
        }

        let data = null;

        /* ======================================
          1️⃣ Coba sebagai documentId
        ====================================== */
        try {
          const resp = await apiFetch(`/api/umats/${id}?populate[0]=qiu_dao_person&populate[1]=area&populate[2]=profilepic&populate[3]=avatarpic`);
          data = resp?.data || resp;
        } catch (err) {
          // Jika bukan 404, lempar error
          if (err.status && err.status !== 404) {
            throw err;
          }
        }

        /* ======================================
          2️⃣ Fallback: cari berdasarkan field umatID
        ====================================== */
        if (!data) {
          const encoded = encodeURIComponent(id);

          const listUrl =
            `/api/umats?filters%5BumatID%5D%5B$eq%5D=${encoded}?populate[0]=qiu_dao_person&populate[1]=area&populate[2]=profilepic&populate[3]=avatarpic`;

          const resp2 = await apiFetch(listUrl);

          const arr = resp2?.data;
          if (Array.isArray(arr) && arr.length > 0) {
            data = arr[0];
          }
        }

        /* ======================================
          3️⃣ Jika tetap tidak ada
        ====================================== */
        if (!data) {
          setErrorMsg("Data umat tidak ditemukan.");
          return;
        }

        if (mounted) {
          setUmat(data?.attributes || data);

          // load spouse jika ada
          if (data?.spouseId) {
            try {
              const respSpouse = await apiFetch(`/api/umats/${data.spouseId}`);
              const s = respSpouse?.data || respSpouse;

              setSpouse(s);

              const isReciprocal =
                s?.spouseId && String(s.spouseId) === String(data.documentId);

              setSpouseStatus(isReciprocal ? "SYNC" : "NOT_SYNC");
            } catch (err) {
              console.warn("load spouse failed", err);
            }
          } else {
            setSpouse(null);
            setSpouseStatus(null);
          }

          setErrorMsg(null);
        }

      } catch (e) {
        console.warn("loadUmat failed", e);
        setErrorMsg("Terjadi kesalahan saat memuat data.");
      }
    }

    loadUmat();
    return () => { mounted = false; };
  }, [id]);

  const tabs = Object.keys(UMAT_DETAIL_PANEL).map(k => ({
    key: k,
    label: isZh() ? TAB_LABELS[k].zh : TAB_LABELS[k].id
  }));

  return (
    <TwoColumnLayout
      variant="oneColumn"
      title={isZh() ? "個人詳細資料" : "Profil Detil"}
      showBack
      onBack={() => history.goBack()}
      rightNode={
        <MainPanel
          umat={umat}
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          errorMsg={errorMsg}
          familyData={familyData}
          familyLoading={familyLoading}
          siblings={siblings}
          children={children} 
          spouse={spouse}
          spouseStatus={spouseStatus}
          />
      }
    />
  );
}

/* ===============================
   MAIN PANEL
================================ */
function MainPanel({ umat, tabs, activeTab, onTabChange, errorMsg, familyData, familyLoading, siblings, children, spouse, spouseStatus }) {
  // ===============================
  // ERROR STATE
  // ===============================
  if (errorMsg) {
    return (
      <div style={{
        padding: "2rem",
        textAlign: "center",
        color: "#b91c1c"
      }}>
        {errorMsg}
      </div>
    );
  }

  // ===============================
  // LOADING STATE
  // ===============================
  if (!umat) {
    return (
      <div style={{
        padding: "2rem",
        textAlign: "center",
        color: "#64748b"
      }}>
        {isZh() ? "載入中..." : "Memuat data..."}
      </div>
    );
  }

  // ===============================
  // DATA EXISTS (NORMAL RENDER)
  // ===============================
  const level = computeDutyLevel(umat);
  const badge = DUTY_BADGE[level];

  const fields = UMAT_DETAIL_PANEL[activeTab];

  const visibleFields = fields.filter((f) => {
    if (activeTab === "perjalanan_tao") {
      const val = getPerjalananTaoValue(umat, f);
      return val !== null && val !== undefined && val !== "";
    }

    const v = umat[f];
    return v !== null && v !== undefined && v !== "";
  });

  const areaLabel =
    umat?.area?.name ||
    umat?.area?.shortname ||
    (typeof umat?.area === "string" ? umat.area : null);
      
  return (
    <div>
      {/* ===== HEADER ===== */}
      <div
        style={{
          background: "#fff",
          borderRadius: "0.75rem",
          border: "1px solid #e6eef8",
          padding: "1.25rem",
          marginBottom: "1rem"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "1.5rem",
            alignItems: "flex-start",
            flexWrap: "wrap"
          }}
        >
          {/* FOTO */}
          <div>
            <img
              src={umat.profilepic?.url || getAvatarPlaceholder(umat.gender)}
              alt="avatar"
              style={{
                width: "9.75rem",   // 156px
                height: "9.75rem",
                objectFit: "cover",
                borderRadius: "0.5rem",
                border: "1px solid #e5e7eb"
              }}
            />
          </div>

          {/* INFO */}
          <div style={{ flex: 1, minWidth: "14rem" }}>
            <div style={{ fontSize: "1.125rem", fontWeight: 700 }}>
              {umat.umatID} | {getUmatDisplayLabel(umat)}
            </div>

            <div style={{ fontSize: "1rem", marginTop: "0.25rem" }}>
              {getUmatDisplayLabelLine2(umat)}
            </div>

            <div
              style={{
                fontSize: "0.875rem",
                marginTop: "0.25rem",
                color: "#64748b"
              }}
            >
              {areaLabel || umat.district || umat.address || "-"}
            </div>

            {/* BADGE DI BARIS BARU */}
            {badge && (
              <div style={{ marginTop: "0.75rem" }}>
                <span
                  style={{
                    padding: "0.35rem 0.85rem",
                    borderRadius: "999px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: badge.color,
                    background: badge.bg,
                    display: "inline-block"
                  }}
                >
                  {badge.label}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== TABS ===== */}
      <div style={{ margin: "0.75rem 0" }}>
        <TopNavTabs
          items={tabs}
          activeKey={activeTab}
          onTabClick={(k) => {
            onTabChange(k);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>

      {/* ===== CONTENT ===== */}
      <div
        style={{
          background: "#fff",
          borderRadius: "0.5rem",
          border: "1px solid #e6eef8",
          padding: "1rem",
        }}
      >
        {errorMsg ? (
          <div style={{ textAlign: "center", color: "#b91c1c" }}>
            {errorMsg}
          </div>
        ) : !umat ? (
          <div style={{ textAlign: "center", color: "#64748b" }}>
            {isZh() ? "載入中..." : "Memuat data..."}
          </div>
        ) : activeTab === "keluarga" ? (
          <KeluargaTab
            umat={umat}
            familyData={familyData}
            siblings={siblings}
            children={children}
            loading={familyLoading}
            spouse={spouse}
            spouseStatus={spouseStatus}
          />
        ) : activeTab === "perjalanan_tao" ? (
          <PerjalananTaoSection umat={umat} />
        ) : activeTab === "jabatan" ? (
          <JabatanSection umat={umat} />
        ) : visibleFields.length === 0 ? (
          <div style={{ textAlign: "center", color: "#64748b" }}>
            {isZh() ? "尚無資料" : "Informasi belum tersedia"}
          </div>
        ) : (
          visibleFields.map((field) => {
            let value = umat[field];

            if (activeTab === "perjalanan_tao") {
              const raw = getPerjalananTaoValue(umat, field);
              const fromQiu = raw !== umat[field];

              value = fromQiu ? raw + " 🔗" : raw;
            }

            return (
              <FieldRow
                key={field}
                field={field}
                value={value}
              />
            );
          })
        )}
      </div>

    </div>
  );
}

/* ===============================
   READ-ONLY FIELD ROW
================================ */
function FieldRow({ field, value }) {
  if (value === null || value === undefined || value === "") return null;

  const meta = UMAT_FIELD_META[field] || {};
  const label = meta.label
    ? (isZh() ? meta.label.zh : meta.label.id)
    : field;
    const displayValue = meta.format ? meta.format(value) : value;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "clamp(7rem, 35%, 14rem) 1fr",
        padding: "0.4rem 0",
        borderBottom: "1px dashed #e5e7eb",
        fontSize: "0.875rem"
      }}
    >
      <div style={{ color: "#64748b", fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ color: "#0f172a" }}>
        {displayValue}
      </div>
    </div>
  );
}
